<template>
  <div class="CommuneCard" v-if="communeData">
    <div class="CommuneCard__header">
      <div class="CommuneCard__icon">
        <Icon icon="$accountGroup" color="main-blue" :size="32" />
      </div>
      <h3 class="CommuneCard__title">
        {{ communeData.cmn_titre || communeData.nom || 'Commune' }}
      </h3>
    </div>

    <div class="CommuneCard__content">
      <!-- Appartenance territoriale -->
      <div class="CommuneCard__section" v-if="communeData.cmn_region || communeData.cmn_dept">
        <h4 class="CommuneCard__sectionTitle">
          {{ $t('commune.territorialAffiliation', 'APPARTENANCE TERRITORIALE') }}
        </h4>
        <div class="CommuneCard__infoBox">
          <div class="CommuneCard__infoRow" v-if="communeData.cmn_region">
            <span class="CommuneCard__label">{{ $t('commune.region', 'Region') }}:</span>
            <span class="CommuneCard__value">{{ communeData.cmn_region }}</span>
          </div>
          <div class="CommuneCard__infoRow" v-if="communeData.cmn_dept">
            <span class="CommuneCard__label">{{ $t('commune.department', 'Département') }}:</span>
            <span class="CommuneCard__value">{{ communeData.cmn_dept }}</span>
          </div>
          <div class="CommuneCard__infoRow" v-if="communeData.cmn_statut">
            <span class="CommuneCard__label"
              >{{ $t('commune.urbanCommunity', 'Communauté urbaine') }}:</span
            >
            <span class="CommuneCard__value">{{ communeData.cmn_statut }}</span>
          </div>
        </div>
      </div>

      <!-- Administration -->
      <div
        class="CommuneCard__section"
        v-if="
          communeData.cmn_maire ||
          communeData.cmn_boite_postale ||
          communeData.cmn_site_web ||
          communeData.cmn_nbr_conseiller !== undefined ||
          communeData.cmn_nbr_conseiller_femme !== undefined ||
          communeData.cmn_nbr_adjoint !== undefined ||
          communeData.cmn_nbr_adjoint_femme !== undefined ||
          communeData.cmn_date_creation ||
          communeData.cmn_annee_debut ||
          communeData.cmn_annee_fin ||
          communeData.maireInfo?.email ||
          communeData.maireInfo?.telephone ||
          communeData.cmn_email_marie ||
          communeData.cmn_telephone
        "
      >
        <h4 class="CommuneCard__sectionTitle">
          {{ $t('commune.administration', 'ADMINISTRATION') }}
        </h4>
        <div class="CommuneCard__infoBox">
          <div class="CommuneCard__infoRow" v-if="communeData.cmn_maire">
            <span class="CommuneCard__label">{{ $t('commune.mayor', 'Maire') }}:</span>
            <span class="CommuneCard__value">
              <span
                v-if="maireTooltipText"
                class="CommuneCard__maireName"
                :data-tooltip="maireTooltipText"
              >
                {{ communeData.cmn_maire }}
              </span>
              <span v-else>{{ communeData.cmn_maire }}</span>
            </span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="communeData.maireInfo?.email || communeData.cmn_email_marie"
          >
            <span class="CommuneCard__label">{{ $t('commune.email', 'Email') }}:</span>
            <span class="CommuneCard__value">
              <a
                :href="`mailto:${communeData.maireInfo?.email || communeData.cmn_email_marie}`"
                v-if="communeData.maireInfo?.email || communeData.cmn_email_marie"
              >
                {{ communeData.maireInfo?.email || communeData.cmn_email_marie }}
              </a>
            </span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="communeData.maireInfo?.telephone || communeData.cmn_telephone"
          >
            <span class="CommuneCard__label">{{ $t('commune.phone', 'Téléphone') }}:</span>
            <span class="CommuneCard__value">
              <a
                :href="`tel:${communeData.maireInfo?.telephone || communeData.cmn_telephone}`"
                v-if="communeData.maireInfo?.telephone || communeData.cmn_telephone"
              >
                {{ communeData.maireInfo?.telephone || communeData.cmn_telephone }}
              </a>
            </span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="communeData.cmn_annee_debut || communeData.cmn_annee_fin"
          >
            <span class="CommuneCard__label"
              >{{ $t('commune.mandatePeriod', 'Période de mandat') }}:</span
            >
            <span class="CommuneCard__value">
              {{ communeData.cmn_annee_debut || '?' }} - {{ communeData.cmn_annee_fin || '?' }}
            </span>
          </div>
          <div class="CommuneCard__infoRow" v-if="communeData.cmn_boite_postale">
            <span class="CommuneCard__label">{{ $t('commune.address', 'Adresse') }}:</span>
            <span class="CommuneCard__value">{{ communeData.cmn_boite_postale }}</span>
          </div>
          <div class="CommuneCard__infoRow" v-if="communeData.cmn_site_web">
            <span class="CommuneCard__label">{{ $t('commune.website', 'Site') }}:</span>
            <span class="CommuneCard__value">
              <a
                :href="normalizeUrl(communeData.cmn_site_web)"
                target="_blank"
                rel="noopener noreferrer"
                v-if="communeData.cmn_site_web"
              >
                {{ communeData.cmn_site_web }}
              </a>
            </span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="
              communeData.cmn_nbr_conseiller !== undefined &&
              communeData.cmn_nbr_conseiller !== null
            "
          >
            <span class="CommuneCard__label"
              >{{ $t('commune.councillors', 'Nombre de conseillers') }}:</span
            >
            <span class="CommuneCard__value">{{ communeData.cmn_nbr_conseiller }}</span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="
              communeData.cmn_nbr_conseiller_femme !== undefined &&
              communeData.cmn_nbr_conseiller_femme !== null
            "
          >
            <span class="CommuneCard__label"
              >{{ $t('commune.councillorsFemale', 'Nombre de conseillères') }}:</span
            >
            <span class="CommuneCard__value">{{ communeData.cmn_nbr_conseiller_femme }}</span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="communeData.cmn_nbr_adjoint !== undefined && communeData.cmn_nbr_adjoint !== null"
          >
            <span class="CommuneCard__label"
              >{{ $t('commune.deputyMayors', "Nombre d'adjoints") }}:</span
            >
            <span class="CommuneCard__value">{{ communeData.cmn_nbr_adjoint }}</span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="
              communeData.cmn_nbr_adjoint_femme !== undefined &&
              communeData.cmn_nbr_adjoint_femme !== null
            "
          >
            <span class="CommuneCard__label"
              >{{ $t('commune.deputyMayorsFemale', "Nombre d'adjointes") }}:</span
            >
            <span class="CommuneCard__value">{{ communeData.cmn_nbr_adjoint_femme }}</span>
          </div>
          <div class="CommuneCard__infoRow" v-if="communeData.cmn_date_creation">
            <span class="CommuneCard__label"
              >{{ $t('commune.creationDate', 'Date de création') }}:</span
            >
            <span class="CommuneCard__value">{{ formatDate(communeData.cmn_date_creation) }}</span>
          </div>
        </div>
      </div>

      <!-- Population -->
      <div
        class="CommuneCard__section"
        v-if="communeData.cmn_population || communeData.cmn_superficie || communeData.cmn_densite"
      >
        <h4 class="CommuneCard__sectionTitle">
          {{ $t('commune.population', 'POPULATION') }}
        </h4>
        <div class="CommuneCard__infoBox">
          <div
            class="CommuneCard__infoRow"
            v-if="
              communeData.cmn_population !== undefined &&
              communeData.cmn_population !== null &&
              communeData.cmn_population !== ''
            "
          >
            <span class="CommuneCard__label">{{ $t('commune.inhabitants', 'Habitants') }}:</span>
            <span class="CommuneCard__value">{{ formatNumber(communeData.cmn_population) }}</span>
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="
              communeData.cmn_superficie !== undefined &&
              communeData.cmn_superficie !== null &&
              communeData.cmn_superficie !== ''
            "
          >
            <span class="CommuneCard__label">{{ $t('commune.area', 'Superficie') }}:</span>
            <span class="CommuneCard__value"
              >{{ formatNumber(communeData.cmn_superficie) }} km²</span
            >
          </div>
          <div
            class="CommuneCard__infoRow"
            v-if="
              communeData.cmn_densite !== undefined &&
              communeData.cmn_densite !== null &&
              communeData.cmn_densite !== ''
            "
          >
            <span class="CommuneCard__label">{{ $t('commune.density', 'Densité') }}:</span>
            <span class="CommuneCard__value">{{ communeData.cmn_densite }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import Icon from '@/components/ui/Icon.vue'
import { normalizeUrl } from '@/services/utils/UtilsService'

const { t: $t } = useI18n()

export interface CommuneData {
  /** API resource id (UUID) for fetching full commune from GET /api/communes/{id} */
  id?: string
  // Champs de la base de données
  cmn_code?: string
  cmn_titre?: string
  cmn_dept?: string
  cmn_region?: string
  cmn_population?: number | string
  cmn_statut?: string
  cmn_boite_postale?: string
  cmn_site_web?: string
  cmn_superficie?: number | string
  cmn_densite?: string
  cmn_email_marie?: string
  cmn_maire?: string
  cmn_telephone?: string
  cmn_nbr_conseiller?: number
  cmn_nbr_conseiller_femme?: number
  cmn_nbr_adjoint?: number
  cmn_nbr_adjoint_femme?: number
  cmn_date_creation?: string
  cmn_annee_debut?: number
  cmn_annee_fin?: number
  // Informations complètes du maire pour le tooltip
  maireInfo?: {
    nom?: string
    email?: string
    telephone?: string
    role?: string
    parti?: string
    sexe?: string
  }
  // Champs de compatibilité (anciens noms)
  nom?: string
  communeUrbaine?: string
  departement?: string
  region?: string
  maire?: string
  adresse?: string
  couriel?: string
  telephone?: string
  site?: string
  habitants?: string | number
  superficie?: string | number
  densite?: string | number
}

const props = defineProps<{
  communeData: CommuneData | null
}>()

/**
 * Génère le texte du tooltip pour le maire avec les labels
 * Format: Rôle : ... Parti : ... Sexe : ... etc.
 * Note: Email et téléphone ne sont plus dans le tooltip car affichés directement dans la section Administration
 */
const maireTooltipText = computed(() => {
  if (!props.communeData?.maireInfo) {
    return null
  }
  const info = props.communeData.maireInfo
  const parts: string[] = []

  // Ajouter chaque information avec son label (sauf email et téléphone qui sont affichés directement)
  if (info.role) {
    parts.push(`${$t('commune.role', 'Rôle')}: ${info.role}`)
  }
  if (info.parti) {
    parts.push(`${$t('commune.party', 'Parti')}: ${info.parti}`)
  }
  if (info.sexe) {
    parts.push(`${$t('commune.gender', 'Sexe')}: ${info.sexe}`)
  }

  return parts.length > 0 ? parts.join('\n') : null
})

/**
 * Formate un nombre avec des séparateurs de milliers
 */
function formatNumber(value: number | string | undefined | null): string {
  if (value === undefined || value === null || value === '') {
    return ''
  }
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) {
    return String(value)
  }
  return new Intl.NumberFormat('fr-FR').format(num)
}

/**
 * Formate une date au format français
 */
function formatDate(dateString: string | undefined | null): string {
  if (!dateString) {
    return ''
  }
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) {
      return dateString
    }
    return new Intl.DateTimeFormat('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date)
  } catch {
    return dateString
  }
}
</script>

<style lang="scss" scoped>
.CommuneCard {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.CommuneCard__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid rgb(var(--v-theme-main-blue));
}

.CommuneCard__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(var(--v-theme-main-blue-rgb), 0.1);
  border-radius: 50%;
}

.CommuneCard__title {
  color: rgb(var(--v-theme-main-blue));
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  text-transform: uppercase;
}

.CommuneCard__content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.CommuneCard__section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.CommuneCard__sectionTitle {
  color: rgb(var(--v-theme-main-blue));
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  margin: 0;
}

.CommuneCard__infoBox {
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.CommuneCard__infoRow {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.CommuneCard__label {
  font-weight: 600;
  color: #333;
  font-size: 0.875rem;
}

.CommuneCard__value {
  color: #666;
  font-size: 0.875rem;

  a {
    color: rgb(var(--v-theme-main-blue));
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

.CommuneCard__maireName {
  position: relative;
  cursor: help;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  display: inline-block;

  &::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 0;
    transform: translateY(-8px);
    padding: 1rem 1.25rem;
    background: rgba(0, 0, 0, 0.95);
    color: white;
    font-size: 0.8125rem;
    white-space: pre-line;
    line-height: 1.8;
    border-radius: 6px;
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s ease,
      visibility 0.2s ease,
      transform 0.2s ease;
    z-index: 1000;
    min-width: 320px;
    max-width: min(600px, calc(100vw - 2rem));
    width: max-content;
    word-break: break-word;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
    text-align: left;
  }

  // Responsive pour mobile
  @media (max-width: 768px) {
    &::before {
      min-width: 280px;
      max-width: calc(100vw - 2rem);
    }
  }

  // Responsive pour tablette
  @media (min-width: 769px) and (max-width: 1024px) {
    &::before {
      min-width: 300px;
      max-width: 450px;
    }
  }

  // Desktop large
  @media (min-width: 1025px) {
    &::before {
      min-width: 350px;
      max-width: 600px;
    }
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 1.5rem;
    transform: translateY(-2px);
    border: 5px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
    pointer-events: none;
    opacity: 0;
    visibility: hidden;
    transition:
      opacity 0.2s ease,
      visibility 0.2s ease;
    z-index: 1001;
  }

  &:hover::before,
  &:hover::after {
    opacity: 1;
    visibility: visible;
  }

  &:hover::before {
    transform: translateY(-12px);
  }
}
</style>
